import { useFormInstance } from '@neo/blocks/forms/form-provider'
import type { ButtonProps } from '@neo/index'
import type React from 'react'
import { useEffect } from 'react'
import type { RbacModules, RbacPermissionTypes } from 'v2/types/rbac'
import { ButtonWithPermission } from './button-with-permission'

interface FormSubmitButtonProps extends Omit<ButtonProps, 'onClick'> {
  formId: string
  children: React.ReactNode
  loading?: boolean
  disableOnInvalid?: boolean
  onSubmit?: () => void
  useExternalDisable?: boolean
  onClick?: (form: ReturnType<typeof useFormInstance>) => void
  module?: RbacModules
  permissionType?: RbacPermissionTypes
}

export const FormSubmitButton: React.FC<FormSubmitButtonProps> = ({
  formId,
  children,
  disableOnInvalid = false,
  loading = false,
  disabled,
  onSubmit,
  useExternalDisable,
  onClick,
  module,
  permissionType,
  ...buttonProps
}) => {
  useEffect(() => {
    // happy-dom crashes dispatching submit on an undefined listener; browsers ignore it.
    if (!onSubmit) return
    const formElement = document.getElementById(formId)
    if (formElement) {
      formElement.addEventListener('submit', onSubmit as EventListenerOrEventListenerObject)
    }
    return () => {
      if (formElement) {
        formElement.removeEventListener('submit', onSubmit as EventListenerOrEventListenerObject)
      }
    }
  }, [formId, onSubmit])

  const form = useFormInstance(formId)
  if (!form) return null
  return (
    <form.Subscribe
      selector={(state) => [state.isTouched, state.isValid]}
      children={([isTouched, isValid]) => (
        <ButtonWithPermission
          form={formId}
          type="submit"
          disabled={
            useExternalDisable !== undefined
              ? useExternalDisable
              : disabled || !isTouched || (disableOnInvalid && !isValid)
          }
          loading={loading}
          onClick={() => {
            onClick?.(form)
          }}
          module={module}
          permissionType={permissionType}
          {...buttonProps}
        >
          {children}
        </ButtonWithPermission>
      )}
    />
  )
}
